<script>
    import { format, locale } from "svelte-i18n";
    import dayjs from "dayjs";
    import { onDestroy, onMount, tick } from "svelte";
    import { user } from "../../user/user.state.svelte.js";
    import AssetsApi from "../../asset/assets.api.js";
    import summaryService from "../../_summary/service.js";
    import reportService from "../../report/v2-service.js";
    import { notificate } from "../../../services/notification.js";
    import { goto } from "@roxi/routify";

    // TomSelect Imports
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import "dayjs/locale/es";
    import "dayjs/locale/en";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    $goto; // manually initialize the helper
    const redirect = (path) => $goto(path);

    let {
        snapshot,
        filters = {
            language: $locale,
            format: "html",
            asset_tag: "",
            finding_tag: "",
            vulnerability_tag: "",
            asset_kind__in: "",
        },
        handleReportFilters,
        saveNewSnapshot,
        showBtn,
        is_requesting,
        handleReportDownload
    } = $props();

    let summaries = $state([]);
    let summary_id = $state(null);
    let is_showing_btn = $state(showBtn);
    let updateInterval;
    let counter = 0;
    let saveCheckbox = $state(false);
    let reportNotFound = $state(false);

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || $format("page.snapshot.select"),
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    // --- Modal Reset Action ---
    function modalAction(node) {
        const handleHidden = () => {
            resetState();
        };
        node.addEventListener("hidden.bs.modal", handleHidden);

        return {
            destroy() {
                node.removeEventListener("hidden.bs.modal", handleHidden);
            }
        };
    }

    const updateTimeDelta = () => {
        let deltaT;
        if (snapshot) {
            counter++;
            deltaT = dayjs(snapshot.created_at).fromNow();
            if (counter > 120) {
                handleTimeDeltaInterval(3600);
            } else {
                handleTimeDeltaInterval(60);
            }
        }
        return deltaT;
    };

    const handleTimeDeltaInterval = (seconds) => {
        if (updateInterval) clearInterval(updateInterval);
        updateInterval = setInterval(updateTimeDelta, seconds * 1000);
    };

    let timeDelta = $derived.by(updateTimeDelta);

    const resetState = () => {
        filters.asset_tag = "";
        filters.finding_tag = "";
        filters.vulnerability_tag = "";
        filters.asset_kind__in = "";
        filters.format = "html";
        filters.language = $locale || "es";

        saveCheckbox = false;
        summary_id = null;
        reportNotFound = false;
        is_showing_btn = showBtn;

        if (snapshot && snapshot.id) {
            handleReportFilters(snapshot.id, filters);
        }
    };

    const updateShowBtnFlag = () => {
        if (showBtn) {
            is_showing_btn = !is_showing_btn;
        }
    };

    const hideModal = () => {
        const closeBtn = document.querySelector('#snapshot-report-modal .btn-close');
        if (closeBtn) closeBtn.click();
    };

    const reportDownloadCallToAction = async (
        config = { filter: { language: $locale } },
        summary_id,
        should_save = false,
    ) => {
        updateShowBtnFlag();
        await handleReportDownload(config, summary_id, should_save).catch(
            async (e) => {
                let err = e.message;
                if (
                    err.includes(
                        "There are not records matching the search criteria",
                    ) ||
                    err.includes(
                        "No hay resultados para la combinación de etiquetas elegida",
                    )
                ) {
                    reportNotFound = true;
                    await tick();
                }
                updateShowBtnFlag();
            },
        );
        updateShowBtnFlag();

        if (!reportNotFound) {
            hideModal();
        }
    };

    const saveSnapshotCallToActions = () => {
        saveNewSnapshot();
        updateShowBtnFlag();
        handleTimeDeltaInterval();
    };

    const goBack = () => {
        resetState();
    };

    const navigateToSnapshot = () => {
        hideModal();
        // Force cleanup of Bootstrap's overlay because the SPA router instantly
        // destroys the component before the fade animation can finish removing it.
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
        document.body.style.removeProperty('overflow');
        document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());

        redirect("/snapshot");
    };

    const getSummaries = async (config = { filter: {} }) => {
        summaries = await summaryService.search();
        return summaries;
    };

    onMount(async () => {
        if (user.data.is_internal) {
            await getSummaries();
        }
    });

    onDestroy(() => {
        if (updateInterval) clearInterval(updateInterval);

        // Bulletproof cleanup for any component destruction while modal is open
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
        document.body.style.removeProperty('overflow');
        document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    });
</script>

<!-- MODAL -->
<!-- use:modalAction guarantees that when Bootstrap finishes hiding it, resetState() runs -->
<div
    class="modal fade"
    id="snapshot-report-modal"
    tabindex="-1"
    aria-hidden="true"
    data-backdrop="static"
    data-keyboard="false"
    use:modalAction
>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title">
                    {$format("page.snapshot.generate_report_btn")}
                </h3>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onclick={resetState}
                ></button>
            </div>
            <div class="modal-body">
                {#if !is_showing_btn}
                    <div class="overlay-wrapper">
                        <div class="text-center" class:overlay={is_requesting}>
                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                        </div>
                    </div>
                    <div>
                        {#if snapshot.created_at}
                            {$format("page.snapshot.time_delta_text")}
                            {timeDelta}
                            ({dayjs(snapshot["created_at"]).format(
                                "YYYY/MM/DD, hh:mm:ss",
                            )})
                        {/if}
                        {#if reportNotFound}
                            <div class="mt-3 alert-danger p-3">
                                {$format("errors.report_not_found")}
                            </div>
                        {/if}
                    </div>
                    <div class="filters">
                        <div class="mt-3">
                            {$format("page.snapshot.asset_tag_filter")}
                        </div>
                        {#if snapshot.assets_tags && snapshot.assets_tags.length > 0}
                            <select
                                class="form-select"
                                bind:value={filters.asset_tag}
                                oninput={(e) =>
                                    handleReportFilters(snapshot.id, {
                                        asset_tag: e.target.value,
                                    })}
                            >
                                <option value=""
                                    >{$format("page.snapshot.select")}</option
                                >
                                {#each snapshot.assets_tags as tag}
                                    <option value={tag}>{tag}</option>
                                {/each}
                            </select>
                        {:else}
                            <span
                                >{$format("page.snapshot.tags_not_found")}</span
                            >
                        {/if}
                        <div class="mt-3">
                            {$format("page.snapshot.finding_tag_filter")}
                        </div>
                        {#if snapshot.findings_tags && snapshot.findings_tags.length > 0}
                            <select
                                class="form-select"
                                bind:value={filters.finding_tag}
                                oninput={(e) =>
                                    handleReportFilters(snapshot.id, {
                                        finding_tag: e.target.value,
                                    })}
                            >
                                <option value=""
                                    >{$format("page.snapshot.select")}</option
                                >
                                {#each snapshot.findings_tags as tag}
                                    <option value={tag}>{tag}</option>
                                {/each}
                            </select>
                        {:else}
                            <span
                                >{$format("page.snapshot.tags_not_found")}</span
                            >
                        {/if}
                        <div class="mt-3">
                            {$format("page.snapshot.vulnerability_tag_filter")}
                        </div>
                        {#if snapshot.vulnerabilities_tags && snapshot.vulnerabilities_tags.length > 0}
                            <select
                                class="form-select"
                                bind:value={filters.vulnerability_tag}
                                oninput={(e) =>
                                    handleReportFilters(snapshot.id, {
                                        vulnerability_tag: e.target.value,
                                    })}
                            >
                                <option value=""
                                    >{$format("page.snapshot.select")}</option
                                >
                                {#each snapshot.vulnerabilities_tags as tag}
                                    <option value={tag}>{tag}</option>
                                {/each}
                            </select>
                        {:else}
                            <span
                                >{$format("page.snapshot.tags_not_found")}</span
                            >
                        {/if}

                        <div class="mt-3">Filtrar por tipo de activo:</div>
                        <div class="mb-3 mt-3">
                            <select
                                id="asset__kind__in"
                                name="asset_kind_filter"
                                class="form-control"
                                multiple
                                use:tomSelectAction={{
                                    options: AssetsApi.assets_kinds,
                                    value: filters.asset_kind__in ? filters.asset_kind__in.split(',').filter(Boolean) : [],
                                    placeholder: $format("page.snapshot.select"),
                                    onChange: (val) => {
                                        filters.asset_kind__in = val.join(",");
                                        handleReportFilters(snapshot.id, filters);
                                    }
                                }}
                            ></select>
                        </div>
                    </div>
                    <div class="count mt-3">
                        <div class="row">
                            <div class="col-12 col-sm-4 d-flex">
                                <div class="info-box bg-light w-100">
                                    <div class="info-box-content">
                                        <span
                                            class="info-box-text text-center text-muted"
                                            >{$format("page.snapshot.amount")}
                                            {$format(
                                                "asset.plural",
                                            ).toLowerCase()}:</span
                                        >
                                        <span
                                            class="info-box-number text-center text-muted mb-0"
                                            >{snapshot.assets_count}</span
                                        >
                                        {#if filters.asset_tag}<small
                                                class="info-box-text text-center text-muted"
                                                >({$format(
                                                    "page.snapshot.filtering",
                                                )}
                                                {filters.asset_tag})</small
                                            >{/if}
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-4 d-flex">
                                <div class="info-box bg-light w-100">
                                    <div class="info-box-content">
                                        <span
                                            class="info-box-text text-center text-muted"
                                            >{$format("page.snapshot.amount")}
                                            {$format(
                                                "finding.plural",
                                            ).toLowerCase()}:</span
                                        >
                                        <span
                                            class="info-box-number text-center text-muted mb-0"
                                            >{snapshot.findings_count}</span
                                        >
                                        {#if filters.finding_tag}<small
                                                class="info-box-text text-center text-muted"
                                                >({$format(
                                                    "page.snapshot.filtering",
                                                )}
                                                {filters.finding_tag})</small
                                            >{/if}
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-4 d-flex">
                                <div class="info-box bg-light w-100">
                                    <div class="info-box-content">
                                        <span
                                            class="info-box-text text-center text-muted"
                                            >{$format("page.snapshot.amount")}
                                            {$format(
                                                "layout.sidebar.vulnerabilities",
                                            ).toLowerCase()}:</span
                                        >
                                        <span
                                            class="info-box-number text-center text-muted mb-0"
                                            >{snapshot.vulnerabilities_count}</span
                                        >
                                        {#if filters.vulnerability_tag}<small
                                                class="info-box-text text-center text-muted"
                                                >({$format(
                                                    "page.snapshot.filtering",
                                                )}
                                                {filters.vulnerability_tag})</small
                                            >{/if}
                                    </div>
                                </div>
                            </div>
                            <div class="col-12">
                                <button
                                    class="btn btn-default"
                                    onclick={resetState}
                                    ><i class="fas fa-broom"></i>
                                    {$format(
                                        "page.snapshot.clear_filters",
                                    )}</button
                                >
                            </div>
                            {#if user.role != "viewer"}
                                <div class="col-12 mt-3">
                                    <div class="form-check form-switch">
                                        <input
                                            type="checkbox"
                                            bind:checked={saveCheckbox}
                                            class="form-check-input"
                                            id="saveReportCheckbox"
                                        />
                                        <label
                                            class="custom-control-label"
                                            for="saveReportCheckbox"
                                        ></label>{$format("page.report.save")}
                                    </div>
                                </div>
                            {/if}
                            <div class="col-12 mt-3">
                                <label for="reportFormat"
                                    >{$format("list.columns.format")}:</label
                                >
                                <select
                                    class="form-select"
                                    bind:value={filters.format}
                                    style="max-width: 15%;"
                                    id="reportFormat"
                                >
                                    <option value="html">HTML</option>
                                    <option value="pdf">PDF</option>
                                    <option value="markdown">Markdown</option>
                                </select>
                            </div>
                            <div class="col-12 mt-3">
                                <label for="reportLang"
                                    >{$format("form.fields.language")}:</label
                                >
                                <select
                                    class="form-select"
                                    bind:value={filters.language}
                                    style="max-width: 15%;"
                                    id="reportLang"
                                >
                                    <option value="es"
                                        >{$format("spanish")}</option
                                    >
                                    <option value="en"
                                        >{$format("english")}</option
                                    >
                                </select>
                            </div>
                            {#if user.data.is_internal}
                                <div class="col-12 mt-3">
                                    <label for="reportSummary"
                                        >{$format("summary")}:</label
                                    >
                                    <select
                                        class="form-select"
                                        bind:value={summary_id}
                                        style="max-width: 15%;"
                                        id="reportSummary"
                                    >
                                        <option value={null}
                                            >{$format(
                                                "page.snapshot.select",
                                            )}</option
                                        >
                                        {#each summaries as summary}
                                            <option value={summary.id}
                                                >{summary.name}</option
                                            >
                                        {/each}
                                    </select>
                                </div>
                            {/if}
                            <div class="col-12 mt-4">
                                <button
                                    id="snapshotReportSubmit"
                                    type="button"
                                    class="btn btn-primary t-submit-asset-btn"
                                    onclick={(e) => {
                                        e.preventDefault();
                                        reportDownloadCallToAction(
                                            {
                                                filter: filters,
                                            },
                                            summary_id,
                                            saveCheckbox,
                                        );
                                    }}>{$format("buttons.download")}</button
                                >
                                {#if !is_showing_btn && showBtn}<a
                                        href="#"
                                        class="ms-3"
                                        onclick={goBack}
                                        >{$format("page.snapshot.go_back")}</a
                                    >
                                {/if}
                            </div>
                        </div>
                    </div>
                {:else}
                    <div class="callout callout-info bg-light">
                        <p>
                            {$format("page.snapshot.callout")}
                            <a href="#" onclick={navigateToSnapshot}
                                >snapshots</a
                            >.
                        </p>
                    </div>
                    <button
                        type="button"
                        class="btn btn-primary mt-3"
                        onclick={() => saveSnapshotCallToActions()}
                        ><i class="fas fa-camera"></i>
                        {$format("page.snapshot.generate_snapshot_btn")}</button
                    >
                {/if}
            </div>
        </div>
        <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
</div>

<!-- END MODAL-->

<style>
    .overlay-wrapper div {
        display: none;
    }
    .overlay-wrapper div.overlay {
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-top: 20px;
    }

    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>